import { reactive, watch } from "vue";

import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";

type UseEditableFiltersReturn = {
  dataMap: {
    [key: string]: any;
  };
  updateData: (filter: IFilter, options?: IOption[] | IOption) => void;
  emitData: (searchNow?: boolean) => void;
  deleteLastSelectedOption: (filterKey: string) => void;
};

export type EmitDefinitionType<T = { [key: string]: any }, K = any> = {
  onChangedData: (map: T, objectKey?: K) => void;
};

export function useEditableFilters<E extends EmitDefinitionType<any, any>>(
  filters: IFilter[],
  emitDefinition: E,
  objectKey?: any
): UseEditableFiltersReturn {
  /** 필터의 옵션이 변경될 때 해당 필터의 키로 데이터를 저장  */
  const dataMap = reactive<{ [key: string]: any }>({});

  // 초기 availableOptions 셋팅
  filters.forEach((filter) => {
    filter.availableOptions = filter.options.slice();
  });

  const resetChildFieldParmas = (filter: IFilter) => {
    setDataMapValue(filter.key ?? "", null);
    // dataMap에 있는 자식 optionMetadata 값 초기화
    resetChildOptionMetadataParams(filter);
    emitData();
  };

  const resetChildOptionMetadataParams = (filter: IFilter) => {
    const optionMetadata = filter.options?.find(
      (option) => option.optionMetadata
    )?.optionMetadata;
    if (!optionMetadata) return;

    const optionMetadataKeys = Object.keys(optionMetadata);
    if (!optionMetadataKeys.length) return;

    optionMetadataKeys.forEach((key) => {
      setDataMapValue(key, null);
    });
  };

  const isSelectedDirectInput = (filter: IFilter) => {
    if (Array.isArray(filter.selectedOptions)) {
      return filter.selectedOptions.some((option) => option.isDirectInput);
    } else {
      return filter.selectedOptions?.isDirectInput;
    }
  };

  const initializeFilterOptions = (filter: IFilter) => {
    if (!filter) return;

    const parentFilter = filters.find((f) => f.key === filter.parentKey);

    if (!parentFilter) {
      updateOptions(filter, filter.options, undefined);
      return;
    }

    if (isSelectedDirectInput(parentFilter)) {
      addDirectInputOption(filter);
    } else {
      const parentSelectedOption = Array.isArray(parentFilter.selectedOptions)
        ? parentFilter.selectedOptions?.[0]
        : parentFilter.selectedOptions;
      const availableOptions = filter.options.filter(
        (op) => op.parentKey === parentSelectedOption?.key
      );
      updateOptions(filter, availableOptions, undefined);
    }
  };

  const addDirectInputOption = (targetFilter: IFilter) => {
    const directOption: IOption = {
      key: "",
      name: "직접입력",
      isDirectInput: true,
      default: true,
    };
    updateOptions(targetFilter, [directOption], directOption);
  };

  const getParentSelectedOptionsValue = (
    parentFilter?: IFilter,
    selectedOptions?: IOption | IOption[]
  ) => {
    if (!parentFilter || !selectedOptions) return undefined;
    const parentSelectedOptions = Array.isArray(selectedOptions)
      ? selectedOptions
      : [selectedOptions];
    return getValues(parentSelectedOptions, parentFilter.type);
  };

  const watchParentFilterValue = (filter: IFilter, parentFilter: IFilter) => {
    watch(
      () => parentFilter?.selectedOptions,
      async (parentSelectedOptions, parentOldSelectedOptions) => {
        if (parentSelectedOptions === parentOldSelectedOptions) return;

        // 자식 필드값 초기화
        resetChildFieldParmas(filter);
        const parentSelectedOptionsValue = getParentSelectedOptionsValue(
          parentFilter,
          parentSelectedOptions
        );

        initializeFilterOptions(filter);

        // 부모 필드에 선택된 값이 없거나, 부모 필터의 특정 옵션이 선택되지 않았을 경우 초기화
        if (!parentSelectedOptionsValue) {
          updateData(filter, filter.selectedOptions);
          return;
        }
      },
      { deep: true }
    );
  };

  /**
   * Filter 정의로부터 빈 필터 값 할당 및 필요한 경우 watch
   */
  filters.forEach((filter) => {
    if (filter.parentKey) {
      const parentFilter = filters.find((f) => f.key === filter.parentKey);
      if (parentFilter) {
        watchParentFilterValue(filter, parentFilter);
      }
    }
  });

  const getValues = (
    options: IOption[],
    type: FilterKeyValueType
  ): string | string[] | { [key: string]: any }[] => {
    if (type === FilterKeyValueType.FilterKey_OptionValue) {
      const isAllString = options.every(
        (option) => typeof option.value === "string"
      );

      if (isAllString) {
        return options.map((option) => option.value).join(",");
      } else {
        return options.length === 1
          ? options[0].value
          : options.map((option) => option.value);
      }
    }

    // FilterKeyValueType.FilterKey_OptionKey
    return options.map((option) => option.key).join(",");
  };

  const updateMetadata = (options: IOption[]) => {
    options.forEach((option) => {
      if (option.optionMetadata) {
        for (const [key, value] of Object.entries(option.optionMetadata)) {
          dataMap[key] = value;
        }
      }
    });
  };

  const resetMetadata = (filter: IFilter) => {
    const optionMetadata = filter.options.find(
      (option) => option.optionMetadata
    )?.optionMetadata;
    if (!optionMetadata) return;

    const optionMetadataKeys = Object.keys(optionMetadata);
    if (!optionMetadataKeys.length) return;

    optionMetadataKeys.forEach((key) => {
      dataMap[key] = null;
    });
  };

  const setDataMapValue = (key: string, value: any) => {
    dataMap[key] = value;
  };

  const updateMap = (targetFilter: IFilter, selectedOptions?: IOption[]) => {
    if (!selectedOptions) {
      setDataMapValue(targetFilter.key ?? "", null);
      resetMetadata(targetFilter);
      return;
    }

    const values = getValues(selectedOptions, targetFilter.type);
    setDataMapValue(targetFilter.key ?? "", values === "" ? null : values);
    updateMetadata(selectedOptions);
  };

  /** 타겟 필터의 선택 가능한 옵션을 업데이트 */
  const updateOptions = (
    targetFilter: IFilter,
    options: IOption[],
    selectedOptions?: IOption | IOption[]
  ) => {
    targetFilter.availableOptions = options;
    updateSelectedOptions(targetFilter, selectedOptions);
  };

  /** 타겟 필터의 선택된 옵션을 업데이트 */
  const updateSelectedOptions = (
    targetFilter: IFilter,
    selectedOptions?: IOption[] | IOption
  ) => {
    targetFilter.selectedOptions = selectedOptions;
  };

  /**
   * 선택된 옵션으로 dataMap 업데이트를 하고, emit을 한다.
   * @param selectedOptions 선택된 옵션 (단일 또는 복수)
   * @param targetFilter 대상 필터
   * @param searchNow 즉시 검색을 해야 하는지 여부
   * @returns
   */
  const updateData = (
    targetFilter: IFilter,
    selectedOptions?: IOption[] | IOption
  ) => {
    // ToDo: updateSelectedOptions 호출시 watch 함수가 실행되므로 emitData 와 실행순서가 보장되지 않는 문제 해결 필요
    updateSelectedOptions(targetFilter, selectedOptions);

    // Keyword와 결합된 필터는 watchSearchKeywordOption 의 watch에서 처리
    if (targetFilter.type !== FilterKeyValueType.OptionKey_KeywordValue) {
      if (!selectedOptions) {
        updateMap(targetFilter, undefined);
      } else {
        const options = Array.isArray(selectedOptions)
          ? selectedOptions
          : [selectedOptions];
        updateMap(targetFilter, options);
      }
    }

    emitData();
    setTimeout(() => {
      if (targetFilter.callback) {
        targetFilter.callback(selectedOptions);
      }
    }, 100);
  };

  const emitData = () => {
    if (!emitDefinition || !emitDefinition.onChangedData) return;
    setTimeout(() => {
      emitDefinition.onChangedData(dataMap, objectKey);
    }, 100);
  };

  const deleteLastSelectedOption = (filterKey: string) => {
    const targetFilter = Object.values(filters).find(
      (filter) => filter.key === filterKey
    );

    if (!targetFilter || !targetFilter.selectedOptions) return;
    if (Array.isArray(targetFilter.selectedOptions)) {
      targetFilter.selectedOptions.pop();
    } else {
      updateSelectedOptions(targetFilter, undefined);
    }
    updateData(targetFilter, targetFilter.selectedOptions);
  };

  return {
    dataMap,
    updateData,
    emitData,
    deleteLastSelectedOption,
  };
}
